import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, getDocs, doc, updateDoc, deleteDoc, serverTimestamp, where, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { motion } from 'framer-motion';
import { FaUser, FaUserClock, FaUserShield, FaChartBar, FaDownload, FaEdit, FaUnlock, FaLock } from 'react-icons/fa';
import Toast from '../../components/common/Toast';

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [toast, setToast] = useState(null);
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);
  const [resetConfirmStep, setResetConfirmStep] = useState(0);
  const [selectedRole, setSelectedRole] = useState('all');
  const [showBanModal, setShowBanModal] = useState(false);
  const [banReason, setBanReason] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    adminCount: 0,
    editorCount: 0,
    newUsersThisMonth: 0,
    lastMonthUsers: 0
  });

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      let q = collection(db, 'users');
      
      // Filtreleme
      if (selectedRole !== 'all') {
        q = query(q, where('role', '==', selectedRole));
      }

      // Sıralama
      q = query(q, orderBy('createdAt', 'desc'));
      
      const querySnapshot = await getDocs(q);
      const usersList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersList);

      // İstatistikleri hesapla
      const now = new Date();
      const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);

      const newStats = {
        totalUsers: usersList.length,
        activeUsers: usersList.filter(user => {
          const lastLogin = user.lastLoginAt?.toDate?.();
          return lastLogin && lastLogin > lastMonth;
        }).length,
        adminCount: usersList.filter(user => user.role === 'admin').length,
        editorCount: usersList.filter(user => user.role === 'editor').length,
        newUsersThisMonth: usersList.filter(user => {
          const createdAt = user.createdAt?.toDate?.();
          return createdAt && createdAt > thisMonth;
        }).length,
        lastMonthUsers: usersList.filter(user => {
          const createdAt = user.createdAt?.toDate?.();
          return createdAt && createdAt > lastMonth && createdAt < thisMonth;
        }).length
      };

      setStats(newStats);
    } catch (err) {
      console.error('Kullanıcılar yüklenirken hata:', err);
      setError('Kullanıcılar yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [selectedRole]);

  useEffect(() => {
    const fetchData = async () => {
      await fetchUsers();
    };
    fetchData();
  }, [fetchUsers]);

  const handleBanUser = async (userId, isBanned, reason = '') => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        isBanned: isBanned,
        banReason: reason,
        updatedAt: serverTimestamp()
      });

      setUsers(users.map(user => {
        if (user.id === userId) {
          return { ...user, isBanned, banReason: reason };
        }
        return user;
      }));

      setToast({
        message: isBanned ? 'Kullanıcı başarıyla engellendi' : 'Kullanıcının engeli kaldırıldı',
        type: 'success'
      });

      setBanReason('');
      setShowBanModal(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Kullanıcı engelleme hatası:', error);
      setToast({
        message: 'Bir hata oluştu',
        type: 'error'
      });
    }
  };

  const handleBanClick = (user) => {
    if (user.isBanned) {
      // Engel kaldırma işlemi direkt yapılsın
      handleBanUser(user.id, false);
    } else {
      // Engelleme için modal açılsın
      setSelectedUser(user);
      setShowBanModal(true);
    }
  };

  const exportUserData = () => {
    const dataToExport = users.map(user => ({
      id: user.id,
      email: user.email,
      role: user.role,
      createdAt: user.createdAt?.toDate()?.toLocaleString() || 'N/A',
      lastLoginAt: user.lastLoginAt?.toDate()?.toLocaleString() || 'N/A',
      isBanned: user.isBanned || false,
      banReason: user.banReason || ''
    }));

    const jsonString = JSON.stringify(dataToExport, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `users_export_${new Date().toISOString()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getFilteredUsers = () => {
    return users.filter(user =>
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const handleResetDatabase = async () => {
    try {
      // İlk onay kontrolü
      if (resetConfirmStep === 0) {
        setResetConfirmStep(1);
        return;
      }

      // İkinci onay kontrolü ve sıfırlama işlemi
      if (resetConfirmStep === 1) {
        const collections = ['blogs', 'automations', 'questions'];
        
        for (const collectionName of collections) {
          const querySnapshot = await getDocs(collection(db, collectionName));
          const deletePromises = querySnapshot.docs.map(doc => deleteDoc(doc.ref));
          await Promise.all(deletePromises);
        }

        setToast({
          message: 'Veritabanı başarıyla sıfırlandı',
          type: 'success'
        });

        // İşlem tamamlandıktan sonra state'leri sıfırla
        setResetConfirmStep(0);
        setShowResetConfirmation(false);
      }
    } catch (error) {
      console.error('Veritabanı sıfırlama hatası:', error);
      setToast({
        message: 'Veritabanı sıfırlanırken bir hata oluştu',
        type: 'error'
      });
      // Hata durumunda state'leri sıfırla
      setResetConfirmStep(0);
      setShowResetConfirmation(false);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        role: newRole,
        updatedAt: serverTimestamp()
      });

      setUsers(users.map(user => {
        if (user.id === userId) {
          return { ...user, role: newRole };
        }
        return user;
      }));

      // İstatistikleri güncelle
      const updatedStats = { ...stats };
      if (newRole === 'admin') {
        updatedStats.adminCount++;
      } else if (newRole === 'editor') {
        updatedStats.editorCount++;
      }
      setStats(updatedStats);

      setToast({
        message: `Kullanıcı rolü başarıyla ${newRole === 'admin' ? 'Admin' : newRole === 'editor' ? 'Editör' : 'Kullanıcı'} olarak güncellendi`,
        type: 'success'
      });
    } catch (error) {
      console.error('Rol güncellenirken hata:', error);
      setToast({
        message: 'Rol güncellenirken bir hata oluştu',
        type: 'error'
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
              ))}
            </div>
            <div className="h-96 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded">
          <p className="font-bold">Hata</p>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Toplam Kullanıcı"
              value={stats.totalUsers}
              icon={<FaUser />}
            />
            <StatCard
              title="Aktif Kullanıcılar"
              value={stats.activeUsers}
              icon={<FaUserClock />}
            />
            <StatCard
              title="Admin/Editör Sayısı"
              value={stats.adminCount + stats.editorCount}
              icon={<FaUserShield />}
            />
            <StatCard
              title="Bu Ay Yeni Üyeler"
              value={`${stats.newUsersThisMonth} (+${stats.newUsersThisMonth - stats.lastMonthUsers})`}
              icon={<FaChartBar />}
            />
          </div>
        </div>

        <div className="flex justify-between items-center mb-4">
          <div className="flex space-x-2">
            <button
              onClick={exportUserData}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
            >
              <FaDownload className="mr-2" />
              Dışa Aktar
            </button>
          </div>
        </div>

        {/* Filtreler */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
            <div className="flex items-center space-x-4">
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="rounded-lg border-gray-300 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">Tüm Roller</option>
                <option value="admin">Admin</option>
                <option value="editor">Editör</option>
                <option value="user">Kullanıcı</option>
              </select>
            </div>
            <div className="flex-1 md:max-w-md">
              <input
                type="text"
                placeholder="Kullanıcı ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-lg border-gray-300 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Kullanıcı Listesi */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Kullanıcı
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rol
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Kayıt Tarihi
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Son Giriş
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {getFilteredUsers().map((user) => (
                  <motion.tr
                    key={user.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          {user.photoURL ? (
                            <img
                              src={user.photoURL}
                              alt={user.name || user.email}
                              className="h-10 w-10 rounded-full"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                              <FaUser className="h-5 w-5 text-gray-500" />
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.name || 'İsimsiz Kullanıcı'}
                          </div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        user.role === 'admin'
                          ? 'bg-red-100 text-red-800'
                          : user.role === 'editor'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {user.role === 'admin' ? 'Admin' : user.role === 'editor' ? 'Editör' : 'Kullanıcı'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.createdAt?.toDate?.()?.toLocaleString('tr-TR') || 'Belirtilmemiş'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.lastLoginAt?.toDate?.()?.toLocaleString('tr-TR') || 'Belirtilmemiş'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center space-x-3">
                        <div className="relative inline-block text-left">
                          <div>
                            <button
                              type="button"
                              onClick={() => {
                                const dropdown = document.getElementById(`dropdown-${user.id}`);
                                if (dropdown) {
                                  dropdown.classList.toggle('hidden');
                                }
                              }}
                              className={`inline-flex items-center justify-center w-full px-4 py-2 text-sm font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors ${
                                user.role === 'editor'
                                  ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-white hover:from-yellow-600 hover:to-yellow-700 focus:ring-yellow-500'
                                  : 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 focus:ring-green-500'
                              }`}
                            >
                              <FaUserShield className="w-4 h-4 mr-2" />
                              <span>{user.role === 'editor' ? 'Editör' : 'Kullanıcı'}</span>
                              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                          </div>
                          <div
                            id={`dropdown-${user.id}`}
                            className="hidden absolute right-0 mt-2 w-56 rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 divide-y divide-gray-100 focus:outline-none z-10"
                          >
                            <div className="py-1">
                              <button
                                onClick={() => {
                                  handleUpdateRole(user.id, 'user');
                                  document.getElementById(`dropdown-${user.id}`).classList.add('hidden');
                                }}
                                className="group flex items-center w-full px-4 py-3 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 transition-colors"
                              >
                                <FaUser className="w-4 h-4 mr-3 text-green-500" />
                                <span>Kullanıcı</span>
                                {user.role === 'user' && (
                                  <span className="ml-auto bg-green-100 text-green-600 text-xs px-2 py-1 rounded-full">Aktif</span>
                                )}
                              </button>
                              <button
                                onClick={() => {
                                  handleUpdateRole(user.id, 'editor');
                                  document.getElementById(`dropdown-${user.id}`).classList.add('hidden');
                                }}
                                className="group flex items-center w-full px-4 py-3 text-sm text-gray-700 hover:bg-yellow-50 hover:text-yellow-700 transition-colors"
                              >
                                <FaEdit className="w-4 h-4 mr-3 text-yellow-500" />
                                <span>Editör</span>
                                {user.role === 'editor' && (
                                  <span className="ml-auto bg-yellow-100 text-yellow-600 text-xs px-2 py-1 rounded-full">Aktif</span>
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleBanClick(user)}
                          className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            user.isBanned 
                              ? 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700' 
                              : 'bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700'
                          }`}
                        >
                          {user.isBanned ? (
                            <>
                              <FaUnlock className="w-4 h-4 mr-2" />
                              <span>Engeli Kaldır</span>
                            </>
                          ) : (
                            <>
                              <FaLock className="w-4 h-4 mr-2" />
                              <span>Engelle</span>
                            </>
                          )}
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Veritabanı Sıfırlama Butonu */}
      <div className="mt-8">
        <button
          onClick={() => setShowResetConfirmation(true)}
          className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
        >
          Veritabanını Sıfırla
        </button>
      </div>

      {/* Onay Modalı */}
      {showResetConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {resetConfirmStep === 0 ? 'Veritabanını Sıfırla' : 'Son Onay'}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              {resetConfirmStep === 0
                ? 'Bu işlem tüm blog yazılarını, otomasyonları ve soruları kalıcı olarak silecektir. Devam etmek istiyor musunuz?'
                : 'DİKKAT: Bu işlem geri alınamaz! Tüm veriler kalıcı olarak silinecektir. Emin misiniz?'}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowResetConfirmation(false);
                  setResetConfirmStep(0);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                İptal
              </button>
              <button
                onClick={handleResetDatabase}
                className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700"
              >
                {resetConfirmStep === 0 ? 'Devam Et' : 'Evet, Sıfırla'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Engelleme Modalı */}
      {showBanModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Kullanıcıyı Engelle
            </h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Engelleme Sebebi
              </label>
              <textarea
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows="3"
                placeholder="Engelleme sebebini yazın..."
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowBanModal(false);
                  setBanReason('');
                  setSelectedUser(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                İptal
              </button>
              <button
                onClick={() => handleBanUser(selectedUser.id, true, banReason)}
                className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700"
              >
                Engelle
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

const StatCard = ({ title, value, icon }) => (
  <div className="bg-white p-6 rounded-lg shadow-lg">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-600">{title}</p>
        <p className="text-2xl font-semibold mt-2">{value}</p>
      </div>
      <div className="text-3xl text-blue-500">{icon}</div>
    </div>
  </div>
);

export default UserManagement; 